<template>
  <div v-if="expandNavigationDrawer">
    <template v-for="item in navigationItems" :key="item.title">
      <v-list-item
        v-if="!item.children && isNavigationItem(item) && isVisible(item)"
        :class="[
          currentPathEqualsItemPath(item) ? 'nav-item' : '',
          currentPathEqualsItemPath(item) && !item.icon ? 'nav-child-item' : ''
        ]"
        :active="currentPathEqualsItemPath(item)"
        :title="$t(item.title)"
        :prepend-icon="item.icon"
        :value="item"
        :disabled="isDisabledForCurrentLicenseTier(item)"
        :to="item.path"
      >
        <template v-if="item.new" #append>
          <span class="new-badge">{{ $t('common.new') }}</span>
        </template>
        <template
          v-else-if="item.proOnly && !isProAccount && !onPremises()"
          #append
        >
          <div class="pro-badge">
            {{ $t('administration.licenseTiers.pro') }}
          </div>
        </template>
        <template #prepend>
          <v-icon v-if="item.icon" :icon="item.icon" />
        </template>
      </v-list-item>
      <v-list-group
        v-if="item.children && isNavigationGroup(item) && isVisible(item)"
        :value="item.title"
      >
        <template #activator="{ props, isOpen }">
          <v-list-item
            :title="$t(item.title)"
            :prepend-icon="item.icon"
            v-bind="props"
            :value="item"
          >
          </v-list-item>
          <v-divider v-if="isOpen" class="mx-2 my-1" />
        </template>
        <NavigationGroup
          :navigation-items="item.children"
          :expand-navigation-drawer="true"
        />
      </v-list-group>
    </template>
  </div>

  <!-- collapsed NavigationDrawer-->
  <div v-else>
    <template v-for="item in navigationItems" :key="item.title">
      <v-menu open-on-hover location="right" close-on-content-click>
        <template #activator="{ props }">
          <v-list-item
            v-if="
              isVisible(item) &&
              (isNavigationGroup(item) || isNavigationItem(item))
            "
            :class="currentPathIncludesItemOrChild(item) ? 'nav-item' : ''"
            :active="currentPathIncludesItemOrChild(item)"
            :title="$t(item.title)"
            :prepend-icon="item.icon"
            :value="item"
            v-bind="props"
          />
        </template>
        <v-list v-if="item.children">
          <template v-for="(child, index) in item.children" :key="index">
            <v-list-item
              v-if="isVisible(child)"
              :class="currentPathEqualsItemPath(child) ? 'nav-item' : ''"
              :active="currentPathEqualsItemPath(child)"
              :title="$t(child.title)"
              :disabled="isDisabledForCurrentLicenseTier(item)"
              :to="child.path"
            />
          </template>
        </v-list>
        <v-list v-else>
          <v-list-item
            :class="currentPathEqualsItemPath(item) ? 'nav-item' : ''"
            :active="currentPathEqualsItemPath(item)"
            :title="$t(item.title)"
            :disabled="isDisabledForCurrentLicenseTier(item)"
            :to="item.path"
          />
        </v-list>
      </v-menu>
    </template>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import {
  NavigationItem,
  navigationItemVisible
} from '../../../navigationDrawer/navigationDrawer';
import { FeatureFlags } from '@/store/types/auth/index';
import { mapState } from 'pinia';
import { onPremises } from '@/shared/utils';
import { authenticationStore } from '@/store/pinia/AuthenticationStore';

const NavigationGroup = defineComponent({
  name: 'NavigationGroup',
  props: {
    navigationItems: {
      type: Array<NavigationItem>,
      required: true
    },
    expandNavigationDrawer: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      onPremises,
      currentPath: this.$route.path,
      route: this.$route
    };
  },
  computed: {
    ...mapState(authenticationStore, [
      'licensedFeatures',
      'isAdminAccount',
      'isProAccount',
      'isFreeAccount',
      'isUnlicensedAccount',
      'showNotHomeAccountBar'
    ]),
    navigationItemVisible(): typeof navigationItemVisible {
      return navigationItemVisible;
    }
  },
  watch: {
    $route: function (to) {
      this.currentPath = to.path;
    }
  },
  methods: {
    currentPathEqualsItemPath(item: NavigationItem) {
      return this.currentPath === item.path;
    },
    currentPathIncludesItemOrChild(item: NavigationItem) {
      if (item.children) {
        return item.children.some((child) =>
          this.currentPath.includes(child.path)
        );
      } else {
        return this.currentPath.includes(item.path);
      }
    },

    isNavigationItem(item: NavigationItem): boolean {
      return (
        !!item.path && this.checkVisibilityDependingOnLicensedFeatures(item)
      );
    },

    isNavigationGroup(item: NavigationItem): boolean {
      return (
        !item.path && this.checkVisibilityDependingOnLicensedFeatures(item)
      );
    },
    checkVisibilityDependingOnLicensedFeatures(item: NavigationItem): boolean {
      return navigationItemVisible(
        item.featureFlags,
        {
          ...(this.licensedFeatures as FeatureFlags),
          isAdminAccount: this.isAdminAccount,
          isHomeAccount: onPremises() ? true : !this.showNotHomeAccountBar
        },
        item.featureFlagAxiom
      );
    },
    isVisible(item: NavigationItem): boolean {
      return (
        this.isVisibleOnCurrentSystemType(item) &&
        this.checkVisibilityDependingOnLicensedFeatures(item) &&
        this.isVisibleForProOnly(item)
      );
    },
    isVisibleOnCurrentSystemType(item: NavigationItem): boolean {
      if (onPremises()) return item.onlyIfCloud ? !item.onlyIfCloud : true;
      else return item.onlyIfCloud ?? true;
    },
    isDisabledForCurrentLicenseTier(item: NavigationItem): boolean {
      if (this.isUnlicensedAccount)
        return item.visibleForUnlicensed ? false : true;
      else return false;
    },
    isVisibleForProOnly(item: NavigationItem): boolean {
      return this.isProAccount && item.hiddenForPro ? false : true;
    }
  }
});
export default NavigationGroup;
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';
a:hover {
  text-decoration: none;
}
div:deep(.mdi:before) {
  font-size: 16px;
}
div:deep(.v-list-item__prepend) {
  width: 31px !important;
}
div:deep(.v-list-item) {
  padding: 0 10px !important;
  min-height: 32px !important;
  max-height: 32px !important;
}
div:deep(.v-list-group__items .v-list-item) {
  margin: 2px 8px;
  border-radius: 5px !important;
}
div:deep(.v-list-group__items .v-list-item .v-list-item__prepend) {
  width: 23px !important;
}

.new-badge {
  color: colors.$primary-50;
  font-style: italic;
  font-weight: bold;
}
.pro-badge {
  background: -webkit-linear-gradient(colors.$primary, colors.$secondary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 12px;
  font-weight: bold;
}
.nav-child-item::before {
  content: '';
  position: absolute;
  border-left: 3px solid colors.$primary;
  border-radius: 5px !important;
  height: 77%;
  left: 0.4em;
}
.nav-item {
  background-color: colors.$mittelBlau5;
  color: colors.$primary;
}
.nav-item:deep(.v-list-item__overlay) {
  opacity: 0 !important;
}

div:deep(.v-list-item__append) {
  width: 27px !important;
}
</style>
